import semverGt from "semver/functions/gt"
import semverEq from "semver/functions/eq"
import type { UpdateContext } from "~/types"

/**
 * Logs update application messages in development environment only.
 * 
 * This utility function provides debug logging for the update process,
 * helping developers track which migrations are being applied during
 * profile and modifier updates.
 * 
 * @param updateVersion - The version number being applied (e.g., "0.72.0")
 */
function logIfDev(updateVersion: string): void {
  if (import.meta.env.VITE_APP_ENVIRONMENT === "dev") {
    console.log(`Applying update ${updateVersion}... (apply pending updates)`)
  }
}

/**
 * Determines whether a specific update/migration should be applied.
 * 
 * This function implements the core logic for selective update application,
 * allowing for flexible migration strategies:
 * 
 * 1. **Normal updates**: Applied when updateVersion > versionToUpdateFrom
 * 2. **Latest update re-application**: Applied when applyLatestUpdate is true
 *    and updateVersion equals the last seen version
 * 3. **Force all updates**: Applied when applyAllUpdates is true
 * 
 * This flexibility supports scenarios like:
 * - Incremental updates for normal usage
 * - Re-applying the latest migration after fixes
 * - Full migration chains for testing or recovery
 * 
 * @param updateVersion - The version number of the update to potentially apply
 * @param updateContext - Configuration object controlling update behavior
 * @returns True if the update should be applied, false otherwise
 * 
 * @example
 * ```typescript
 * const shouldApply = applyUpdate("0.72.0", {
 *   versionToUpdateFrom: "0.71.0",
 *   applyLatestUpdate: false,
 *   applyAllUpdates: false
 * });
 * // Returns true because 0.72.0 > 0.71.0
 * ```
 */
function applyUpdate(
  updateVersion: string,
  updateContext: UpdateContext
): boolean {
  // Returns true if updateVersion is > versionToUpdateFrom,
  // OR applyLatestUpdate is set to true and updateVersion === appVersionLastSeen,
  // OR applyAllUpdates is set to true.

  if (semverGt(updateVersion, updateContext.versionToUpdateFrom)) {
    logIfDev(updateVersion)
    return true
  }

  if (
    updateContext?.applyLatestUpdate &&
    semverEq(updateVersion, updateContext.versionToUpdateFrom)
  ) {
    logIfDev(updateVersion)
    return true
  }

  if (updateContext?.applyAllUpdates) {
    logIfDev(updateVersion)
    return true
  }

  return false
}

export { applyUpdate }
